import React, { useState, useEffect } from 'react';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../constants/statusColors';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  PeopleOutline,
  BusinessOutlined,
  GroupsOutlined,
  WarningAmberOutlined,
  TrendingUpOutlined,
  PersonAddOutlined,
  AddCircleOutlineOutlined,
  NotificationsActiveOutlined,
  AccountBalance as AuditIcon,
  Security as RiskIcon,
  PersonSearch as NominationIcon,
  AttachMoney as RemunerationIcon,
  Gavel as GovernanceIcon,
  Business as ExecutiveIcon,
  TrendingUp as InvestmentIcon,
  Category as OtherIcon,
  DashboardOutlined as OverviewIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isGroupScopeRole } from '../constants/csAdminRoles';
import { useGlobalPermission } from '../hooks/useGlobalPermission';
import { csAdminApi } from '../services/api';
import DigestSettingsModal from '../components/dashboard/DigestSettingsModal';
import {
  mapCompanyFromApi,
  mapDashboardFromApi,
  mergeExpirySummaryIntoDashboard,
} from '../utils/apiMappers';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { allowed: canCreateCompanies, loading: createPermLoading } = useGlobalPermission('companies', 'create');
  const [stats, setStats] = useState(null);
  const [digestSettingsOpen, setDigestSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('overview');
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const res = await csAdminApi.getCompanies();
      const list = (res.data?.data || []).map(mapCompanyFromApi).map((c) => ({
        id: c.id,
        name: c.name,
        type: c.type || 'Company',
      }));
      setCompanies(list);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);

      const isOverview = selectedCompany === 'overview';
      const selectedCompanyData = companies.find((c) => c.id === parseInt(selectedCompany, 10));

      const companyId = isOverview ? 'overview' : selectedCompany;
      const statsRes = await csAdminApi.getDashboardStats({ companyId });
      let mapped = mapDashboardFromApi(statsRes.data?.data, {
        companies,
        isOverview,
        companyName: selectedCompanyData?.name,
      });
      try {
        const alertsRes = await csAdminApi.getExpiryAlerts({
          company_id: isOverview ? undefined : Number(selectedCompany),
        });
        mapped = mergeExpirySummaryIntoDashboard(mapped, alertsRes.data?.summary);
      } catch (e) {
        console.warn('Expiry summary for dashboard:', e);
      }
      setStats(mapped);
      setError('');
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'DIRECTOR_ADDED':
        return <PersonAddOutlined />;
      case 'EXPIRY_ALERT':
        return <WarningAmberOutlined />;
      case 'COMMITTEE_MEETING':
        return <GroupsOutlined />;
      case 'COMPANY_CREATED':
        return <BusinessOutlined />;
      case 'DOCUMENT_UPLOADED':
        return <AddCircleOutlineOutlined />;
      default:
        return <NotificationsActiveOutlined />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'DIRECTOR_ADDED':
        return '#E3F2FD'; // Light blue background
      case 'EXPIRY_ALERT':
        return '#FFF3E0'; // Light orange background
      case 'COMMITTEE_MEETING':
        return '#F3E5F5'; // Light purple background
      case 'COMPANY_CREATED':
        return ACTIVE_GREEN_BG; // Light green background
      case 'DOCUMENT_UPLOADED':
        return '#E0F7FA'; // Light cyan background
      default:
        return '#F5F5F5'; // Light gray background
    }
  };

  const getActivityIconColor = (type) => {
    switch (type) {
      case 'DIRECTOR_ADDED':
        return '#2196F3'; // Blue icon
      case 'EXPIRY_ALERT':
        return '#FF9800'; // Orange icon
      case 'COMMITTEE_MEETING':
        return '#9C27B0'; // Purple icon
      case 'COMPANY_CREATED':
        return ACTIVE_GREEN; // Green icon
      case 'DOCUMENT_UPLOADED':
        return '#00BCD4'; // Cyan icon
      default:
        return '#757575'; // Gray icon
    }
  };

  const getCommitteeIcon = (committeeName) => {
    if (committeeName.includes('Audit')) return AuditIcon;
    if (committeeName.includes('Risk')) return RiskIcon;
    if (committeeName.includes('Nomination')) return NominationIcon;
    if (committeeName.includes('Remuneration')) return RemunerationIcon;
    if (committeeName.includes('Governance')) return GovernanceIcon;
    if (committeeName.includes('Executive')) return ExecutiveIcon;
    if (committeeName.includes('Investment')) return InvestmentIcon;
    return OtherIcon;
  };

  const getCommitteeColor = (committeeName) => {
    if (committeeName.includes('Audit')) return '#1976D2';
    if (committeeName.includes('Risk')) return '#D32F2F';
    if (committeeName.includes('Nomination')) return '#7B1FA2';
    if (committeeName.includes('Remuneration')) return '#388E3C';
    if (committeeName.includes('Governance')) return '#F57C00';
    if (committeeName.includes('Executive')) return '#0288D1';
    if (committeeName.includes('Investment')) return '#00796B';
    return '#757575';
  };

  const getCommitteeBgColor = (committeeName) => {
    if (committeeName.includes('Audit')) return '#E3F2FD';
    if (committeeName.includes('Risk')) return '#FFEBEE';
    if (committeeName.includes('Nomination')) return '#F3E5F5';
    if (committeeName.includes('Remuneration')) return '#E8F5E9';
    if (committeeName.includes('Governance')) return '#FFF3E0';
    if (committeeName.includes('Executive')) return '#E1F5FE';
    if (committeeName.includes('Investment')) return '#E0F2F1';
    return '#F5F5F5';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
  };

  const isOverview = selectedCompany === 'overview';
  const selectedCompanyData = companies.find(c => c.id === parseInt(selectedCompany));

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Company Secretary Dashboard
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {isOverview 
                ? user?.role === 'COMPANY_SECRETARY'
                  ? `Overview of your ${companies.length} assigned ${companies.length === 1 ? 'company' : 'companies'}`
                  : 'Overview of directors, committees, companies, and expiry management'
                : `Dashboard for ${selectedCompanyData?.name || 'Selected Company'}`
              }
            </Typography>
          </Box>
          
          {/* Company Selector */}
          <Box sx={{ minWidth: 300 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="company-select-label">View Dashboard For</InputLabel>
              <Select
                labelId="company-select-label"
                id="company-select"
                value={selectedCompany}
                label="View Dashboard For"
                onChange={handleCompanyChange}
              >
                <MenuItem value="overview">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OverviewIcon fontSize="small" />
                    <Typography>
                      {user?.role === 'COMPANY_SECRETARY' 
                        ? `Overview (${companies.length} ${companies.length === 1 ? 'Company' : 'Companies'})`
                        : 'Overview (All Companies)'
                      }
                    </Typography>
                  </Box>
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessOutlined fontSize="small" />
                      <Box>
                        <Typography variant="body2">{company.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {company.type}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Statistics Cards - Row 1 */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Directors"
            value={stats?.totalDirectors || 0}
            subtitle={`${stats?.activeDirectors || 0} Active`}
            icon={PeopleOutline}
            color="#2196F3"
            onClick={() => navigate('/directors')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Committees"
            value={stats?.totalCommittees || 0}
            subtitle={`${stats?.activeCommittees || 0} Active`}
            icon={GroupsOutlined}
            color="#9C27B0"
            onClick={() => navigate('/committees')}
          />
        </Grid>
        {isGroupScopeRole(user?.role) && isOverview && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Companies"
                value={stats?.totalCompanies || 0}
                subtitle={`${stats?.pendingCompanies || 0} Pending Setup`}
                icon={BusinessOutlined}
                color={ACTIVE_GREEN}
                onClick={() => navigate('/companies')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Company Secretaries"
                value={stats?.totalCS || 0}
                subtitle={`${stats?.activeCS || 0} Active`}
                icon={PeopleOutline}
                color="#00BCD4"
                onClick={() => navigate('/company-secretaries')}
              />
            </Grid>
          </>
        )}
        {!isOverview && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Company Secretary"
              value={stats?.totalCS || 0}
              subtitle={`${stats?.activeCS || 0} Active`}
              icon={PeopleOutline}
              color="#00BCD4"
              onClick={() => navigate('/company-secretaries')}
            />
          </Grid>
        )}
        {(user?.role === 'COMPANY_SECRETARY' || !isOverview) && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Expiry Alerts"
              value={stats?.expiryAlerts?.total || 0}
              subtitle={`${stats?.expiryAlerts?.overdue || 0} Overdue, ${stats?.expiryAlerts?.urgent || 0} Urgent`}
              icon={WarningAmberOutlined}
              color="#FF9800"
              onClick={() => navigate('/expiry-alerts')}
            />
          </Grid>
        )}

        {/* Expiry Alerts Overview */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Expiry Alerts Overview
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate('/expiry-alerts')}
              >
                View All
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#C8102E',
                    border: '1px solid #a00d24',
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
                    {stats?.expiryAlerts?.overdue || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                    Overdue
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#FFCDD2',
                    border: '1px solid #EF9A9A',
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#C62828', fontWeight: 600 }}>
                    {stats?.expiryAlerts?.urgent || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#991b1b' }}>
                    Urgent (&lt; 30 days)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#FFE0B2',
                    border: '1px solid #FFCC80',
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#E65100', fontWeight: 600 }}>
                    {stats?.expiryAlerts?.warning || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9a3412' }}>
                    Warning (30-60 days)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: ACTIVE_GREEN_BG,
                    border: '1px solid rgba(127, 219, 131, 0.45)',
                  }}
                >
                  <Typography variant="h4" sx={{ color: ACTIVE_GREEN, fontWeight: 600 }}>
                    {stats?.expiryAlerts?.total - stats?.expiryAlerts?.overdue - stats?.expiryAlerts?.urgent - stats?.expiryAlerts?.warning || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Valid (&gt; 60 days)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Upcoming Committee Meetings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Upcoming Committee Meetings
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate('/committees')}
              >
                View All
              </Button>
            </Box>
            <List>
              {stats?.upcomingMeetings?.map((meeting, index) => {
                const CommitteeIcon = getCommitteeIcon(meeting.committee);
                return (
                  <ListItem
                    key={meeting.id}
                    sx={{
                      borderBottom: index < stats.upcomingMeetings.length - 1 ? '1px solid #E0E0E0' : 'none',
                      px: 0,
                    }}
                  >
                    <ListItemAvatar>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          bgcolor: getCommitteeBgColor(meeting.committee),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CommitteeIcon sx={{ fontSize: 24, color: getCommitteeColor(meeting.committee) }} />
                      </Box>
                    </ListItemAvatar>
                    <ListItemText
                      primary={meeting.committee}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                          <Chip
                            label={new Date(meeting.date).toLocaleDateString('en-GB')}
                            size="small"
                            sx={{
                              bgcolor: '#F5F5F5',
                              color: '#616161',
                              fontWeight: 500,
                              fontSize: '0.7rem',
                              height: 20,
                              borderRadius: '10px',
                              border: 'none',
                            }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            {meeting.type}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
              {(!stats?.upcomingMeetings || stats.upcomingMeetings.length === 0) && (
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  No upcoming meetings scheduled
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Recent Activity
              </Typography>
              <Button
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => setDigestSettingsOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Email Digest
              </Button>
            </Box>
            <List>
              {stats?.recentActivity?.map((activity, index) => (
                <ListItem
                  key={activity.id}
                  sx={{
                    borderBottom: index < stats.recentActivity.length - 1 ? '1px solid #E0E0E0' : 'none',
                    px: 0,
                  }}
                >
                  <ListItemAvatar>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: getActivityColor(activity.type),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box sx={{ color: getActivityIconColor(activity.type), display: 'flex' }}>
                        {getActivityIcon(activity.type)}
                      </Box>
                    </Box>
                  </ListItemAvatar>
                    <ListItemText
                      primary={activity.message}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                          {isOverview && activity.company && (
                            <>
                              <Chip
                                label={activity.company}
                                size="small"
                                icon={<BusinessOutlined sx={{ fontSize: '0.9rem' }} />}
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  bgcolor: '#E3F2FD',
                                  color: '#1976D2',
                                }}
                              />
                              <Typography variant="caption" color="textSecondary">
                                •
                              </Typography>
                            </>
                          )}
                          <Typography variant="caption" color="textSecondary">
                            {activity.user}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            •
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatTimestamp(activity.timestamp)}
                          </Typography>
                        </Box>
                      }
                    />
                </ListItem>
              ))}
              {(!stats?.recentActivity || stats.recentActivity.length === 0) && (
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  No recent activity
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        {isGroupScopeRole(user?.role) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<PersonAddOutlined />}
                  onClick={() => navigate('/directors/add')}
                >
                  Add Director
                </Button>
                <Button
                  variant="contained"
                  startIcon={<GroupsOutlined />}
                  onClick={() => navigate('/committees/create')}
                >
                  Create Committee
                </Button>
                {!createPermLoading && canCreateCompanies && (
                  <Button
                    variant="contained"
                    startIcon={<BusinessOutlined />}
                    onClick={() => navigate('/companies/create')}
                  >
                    Create Company
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<PeopleOutline />}
                  onClick={() => navigate('/company-secretaries/create')}
                >
                  Add Company Secretary
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Digest Settings Modal */}
      <DigestSettingsModal
        open={digestSettingsOpen}
        onClose={() => setDigestSettingsOpen(false)}
        onSettingsSaved={() => {
          // Optionally refresh dashboard data
        }}
      />
    </Box>
  );
};

const StatCard = ({ title, value, subtitle, icon: Icon, color, onClick }) => (
  <Card
    sx={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick
        ? {
            boxShadow: 3,
            transform: 'translateY(-2px)',
            transition: 'all 0.2s',
          }
        : {},
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h3" component="div" sx={{ mb: 1, fontWeight: 600 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}15`,
            borderRadius: '50%',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 36, color }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export default Dashboard;
